import axios from "axios";

// Self-healing base URL: if the configured backend is this same site (or its
// apex/www twin), use relative "/api" — served by the Vercel proxy rewrite.
// Avoids cross-origin apex↔www redirects that browsers block.
const configured = process.env.REACT_APP_BACKEND_URL;

function isSameSite(url) {
  try {
    const h = new URL(url).hostname;
    const cur = window.location.hostname;
    return h === cur || h === `www.${cur}` || `www.${h}` === cur;
  } catch {
    return true; // unset or malformed → same-origin relative
  }
}

export const BACKEND_URL = isSameSite(configured) ? "" : configured;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

// During deploy rollovers the edge can answer /api/* with the SPA index.html (200, text/html).
// Treat any non-JSON success as a failure so callers fall into their catch paths instead of
// crashing on `string.coins_total` etc.
api.interceptors.response.use((res) => {
  const ct = String(res.headers?.["content-type"] || "");
  const isBlob = res.config?.responseType === "blob" || res.config?.responseType === "arraybuffer";
  if (!isBlob && (ct.includes("text/html") || (typeof res.data === "string" && /^\s*<!doctype html|^\s*<html/i.test(res.data)))) {
    const err = new Error("API returned HTML instead of JSON (deploy in progress or edge cache) — code HTML-RESPONSE");
    err.code = "HTML_RESPONSE";
    err.config = res.config;
    err.response = { ...res, data: { detail: "SolSpur is finishing an update — try again in a few seconds." } };
    return Promise.reject(err);
  }
  return res;
});

// Human-readable failure text incl. a short code the user can report back.
export function describeApiError(e, fallback = "Something went wrong.") {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg).filter(Boolean).join("; ") || fallback;
  if (e?.code === "ECONNABORTED") return `${fallback} The server took too long to answer (code TIMEOUT).`;
  if (!e?.response) return `${fallback} Couldn't reach SolSpur's servers — check your connection (code NETWORK).`;
  if (status === 403) return `${fallback} The request was blocked by a security filter (code HTTP-403).`;
  if (status === 429) return "Slow down — too many attempts. Wait a minute and retry.";
  if (status >= 500) return `${fallback} Server error (code HTTP-${status}).`;
  return `${fallback} (code HTTP-${status})`;
}

// Failure beacon: GET so it lands even where POST/JSON requests are being blocked.
const _beaconAt = {};
api.interceptors.response.use(undefined, (e) => {
  try {
    const url = e?.config?.url || "";
    if (!url.includes("client-error") && !url.includes("auth/me") && !url.includes("watchers")) {
      const key = `${e?.config?.method}:${url}`;
      const now = Date.now();
      if (!_beaconAt[key] || now - _beaconAt[key] > 30000) {
        _beaconAt[key] = now;
        const q = new URLSearchParams({
          m: (e?.config?.method || "").toUpperCase(),
          u: url.slice(0, 120),
          s: String(e?.response?.status || e?.code || "NETWORK"),
          e: String(e?.message || "").slice(0, 200),
          p: window.location.pathname,
        });
        const img = new Image();
        img.src = `${API}/client-error?${q.toString()}`;
      }
    }
  } catch (_) { /* never let telemetry break the app */ }
  return Promise.reject(e);
});

// Anonymous paper-trading wallet id — non-sensitive (real auth uses httpOnly cookies).
const KEY = "emg_wallet_id";
export function getWalletId() {
  let id = localStorage.getItem(KEY);
  if (!id) {
    const uuid = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
    id = "w_" + uuid.replace(/-/g, "").slice(0, 16);
    localStorage.setItem(KEY, id);
  }
  return id;
}
